import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { conversationsService } from '@services';
import { handleApiError } from '@helpers/api';
import usePermissions from './usePermissions';

const SETTINGS_FULL = 'settings:full';
const MESSAGING_PERMISSIONS = ['messaging:restricted', 'messaging:full'];

export const userCoversTeam = (user, teamId) => {
    if (user?.isOwner) return true;
    return (user?.teams || []).some(t => t.id === teamId || (t.permissions || []).includes(SETTINGS_FULL));
};

// Inbox module gate: owner or a team granting messaging — collaborator grants alone don't confer access.
export const userHasInboxAccess = user => {
    if (user?.isOwner) return true;
    return (user?.teams || []).some(t => (t.permissions || []).some(p => MESSAGING_PERMISSIONS.includes(p)));
};

// Several inbox surfaces (rail, header, drawer) mount together — dedupe only the
// in-flight request, keyed by account so an account switch never reuses another
// account's response. Results are not cached: a remount sees fresh teams.
let teamsRequest = null;
let teamsRequestAccountId = null;
const fetchInboxTeams = accountId => {
    if (!teamsRequest || teamsRequestAccountId !== accountId) {
        teamsRequestAccountId = accountId;
        teamsRequest = conversationsService
            .getInboxTeams()
            // Degrade to "no teams" rather than staying unloaded forever: an
            // unloaded state pins ?team= links to an unvalidated scope and hides
            // the inbox behind an empty list with no error
            .catch(error => {
                handleApiError(error);
                return [];
            })
            .finally(() => {
                teamsRequest = null;
                teamsRequestAccountId = null;
            });
    }
    return teamsRequest;
};

const hasTeamMessaging = team =>
    (team.permissions || []).some(permission => MESSAGING_PERMISSIONS.includes(permission));

// Account teams for the inbox, from the messaging-gated endpoint (GET /api/teams is
// settings-gated). Rail scoping uses the caller's own teams unless they are an inbox
// admin; handover targets are all messaging teams — escalating into a team you are
// not a member of is the point.
export const useInboxTeams = () => {
    const [accountTeams, setAccountTeams] = useState(null);

    const currentUser = useSelector(state => state.auth.user);
    const { hasFullSettings } = usePermissions();
    const isInboxAdmin = hasFullSettings();
    const accountId = currentUser?.accountId;

    useEffect(() => {
        let cancelled = false;
        fetchInboxTeams(accountId).then(teams => {
            if (!cancelled) setAccountTeams(teams);
        });
        return () => {
            cancelled = true;
        };
    }, [accountId]);

    const teams = useMemo(
        () =>
            isInboxAdmin
                ? (accountTeams || []).filter(team => team.hasMessagingAccess)
                : (currentUser?.teams || []).filter(hasTeamMessaging),
        [isInboxAdmin, accountTeams, currentUser],
    );

    const messagingTeams = useMemo(() => (accountTeams || []).filter(team => team.hasMessagingAccess), [accountTeams]);

    return { teams, messagingTeams, teamsLoaded: accountTeams !== null, isInboxAdmin };
};
